import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFragment } from "react-relay";

import { useModalTransition } from "@whoosh/design-system/src/components/overlay/Modal/ModalTransitionContext";
import { SquareCard } from "@whoosh/features/src/Square/features/SquareCardEntry/types";

import { SquareCardEntryModal } from "../../../../../../../features/Square/SquareCardEntryModal/SquareCardEntryModal";
import { useCreateUserCardOnFile } from "../../../../../../../features/Square/SquareCardEntryModal/hooks/useCreateUserCardOnFile";
import { isPaymentMethodEntryModalOpenAtom } from "../../atoms/stores";
import { useUpdatePaymentMethodOnBooking } from "../../hooks/useUpdatePaymentMethodOnBooking/useUpdatePaymentMethodOnBooking";
import { BookingPaymentMethodSelectModal } from "../BookingPaymentMethodSelect/components/BookingPaymentMethodSelectModal";
import { PaymentCardEntryModals_booking$key } from "./__generated__/PaymentCardEntryModals_booking.graphql";

type Props = {
  bookingRef: PaymentCardEntryModals_booking$key;
};

type Phase = "closed" | "choosing" | "transitioning" | "entering" | "applying";

export const PaymentCardEntryModals = ({ bookingRef }: Props) => {
  const { runAfterIdle } = useModalTransition();
  const [shouldModalBeOpen, setShouldModalBeOpen] = useAtom(
    isPaymentMethodEntryModalOpenAtom
  );
  const [routeEmptyCardsToEntry, setRouteEmptyCardsToEntry] = useState(true);
  const [phase, setPhase] = useState<Phase>("closed");
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isLoading, setLoading] = useState(false);
  const savedCardId = useRef<string | undefined>(undefined);

  const booking = useFragment(
    PaymentCardEntryModalsBookingFragmentString,
    bookingRef
  );
  const [createSquareCard, isCreatingSquareCard] = useCreateUserCardOnFile();

  const close = useCallback(() => {
    setPhase("closed");
    setErrorMessage(undefined);
    setShouldModalBeOpen(false);
    savedCardId.current = undefined;
  }, [setShouldModalBeOpen]);

  const [updatePaymentMethodOnBooking, isUpdatingPaymentMethod] =
    useUpdatePaymentMethodOnBooking({
      bookingId: booking.id,
      successCallback: close,
      errorCallback: () => {
        setErrorMessage(
          savedCardId.current
            ? t`Your card was saved, but we couldn’t use it for this booking. Select it below to try again.`
            : t`We couldn’t update the payment method for this booking. Select a card to try again.`
        );
        setPhase("choosing");
      },
    });

  const selectCard = (cardId: string) => {
    setRouteEmptyCardsToEntry(false);
    setErrorMessage(undefined);
    setPhase("applying");
    updatePaymentMethodOnBooking({ paymentCardOnFileId: cardId });
  };

  const openSquareCardEntryModal = useCallback(() => {
    setRouteEmptyCardsToEntry(false);
    setErrorMessage(undefined);
    setPhase("transitioning");
    runAfterIdle(() => setPhase("entering"));
  }, [runAfterIdle]);

  useEffect(() => {
    if (shouldModalBeOpen && phase === "closed") {
      setErrorMessage(undefined);
      setRouteEmptyCardsToEntry(true);
      setPhase("choosing");
    }
  }, [shouldModalBeOpen, phase]);

  return (
    <>
      <BookingPaymentMethodSelectModal
        bookingDate={booking.startSlot?.agenda?.date ?? ""}
        isOpen={phase === "choosing" || phase === "applying"}
        setIsOpen={(isOpen) => {
          if (!isOpen && phase === "choosing") close();
        }}
        errorMessage={errorMessage}
        loading={phase === "applying"}
        selectedPaymentMethodId={booking.paymentCardOnFile?.id}
        setSelectedPaymentMethodId={selectCard}
        openSquareCardEntryModal={openSquareCardEntryModal}
        onNoEligibleCards={
          routeEmptyCardsToEntry ? openSquareCardEntryModal : undefined
        }
      />
      {phase === "entering" ? (
        <SquareCardEntryModal
          isOpen
          onCardEntryCancel={() => setPhase("choosing")}
          onCardEntryError={(message) => {
            setErrorMessage(message);
            setPhase("choosing");
          }}
          onCardEntryComplete={(squareCard: SquareCard) =>
            createSquareCard({ squareNonce: squareCard.nonce })
          }
          onCardEntrySaved={(cardId) => {
            savedCardId.current = cardId;
            selectCard(cardId);
          }}
          loading={isLoading || isCreatingSquareCard || isUpdatingPaymentMethod}
          setLoading={setLoading}
        />
      ) : null}
    </>
  );
};
export const PaymentCardEntryModalsBookingFragmentString = graphql`
  fragment PaymentCardEntryModals_booking on Booking {
    id
    ... on StandardBooking {
      status
    }
    paymentCardOnFile {
      id
      cardBrand
      lastFour
      expMonth
      expYear
    }
    startSlot {
      agenda {
        date
      }
    }
  }
`;
